import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Basket,
  Flight,
  Itinerary,
  PassengerData,
  SeatSelection,
} from './types';
import { BaggageData, Journey, Passenger } from '../baggage2/CommonTypes';
import { ThemeInterfaces } from '../seatmap/CommonInterfaces';
import { FormattedMessage } from 'react-intl';

// Props for the main container component
interface UpsellSummaryContainerProps {
  passengers: PassengerData;
  flights: Flight[];
  basket?: Basket;
  baggageJourneys?: Journey[];
  baggageData: BaggageData;
  trigger?: 'seat' | 'baggage' | 'identity_document';
  theme?: ThemeInterfaces.Theme;
}

// Props for the presentation component
interface UpsellSummaryProps extends UpsellSummaryContainerProps {}

// Types for journey display components
interface JourneyRouteProps {
  from: string;
  fromCity?: string;
  to: string;
  toCity?: string;
}

// Props for passenger-specific components
interface PassengerItemProps extends React.HTMLAttributes<HTMLDivElement> {
  passenger: Passenger;
  trigger: 'seat' | 'baggage' | 'identity_document';
  flights?: Flight[];
  baggageJourneys?: Journey[];
  findSeatOnFlight: (
    passengerId: string,
    flightId: string
  ) => SeatSelection | undefined;
  getPassengerBagsOnJourney: (
    passengerId: string,
    journeyIds: string[]
  ) => React.ReactNode;
}

// #region Journey route component
const JourneyRoute = React.memo<JourneyRouteProps>(
  ({ from, fromCity, to, toCity }) => (
    <span className="gr-flex-1 gr-text-base">
      {fromCity ? (
        <>
          <span>{fromCity}</span>{' '}
          <span className="gr-hidden md:gr-inline">({from})</span>
        </>
      ) : (
        <span>{from}</span>
      )}{' '}
      to{' '}
      {toCity ? (
        <>
          <span>{toCity}</span>{' '}
          <span className="gr-hidden md:gr-inline">({to})</span>
        </>
      ) : (
        <span>{to}</span>
      )}
    </span>
  )
);
JourneyRoute.displayName = 'JourneyRoute';

// #region Seat display component
const SeatDisplay = React.memo<{ seat?: SeatSelection }>(({ seat }) => (
  <span
    className={`${seat?.name ? 'gr-font-bold' : 'gr-text-gray-550'} gr-text-base`}
  >
    {seat?.name ? (
      `${seat.name}`
    ) : (
      <FormattedMessage
        id="no_seat_selection"
        defaultMessage="No seat selected"
      />
    )}
  </span>
));
SeatDisplay.displayName = 'SeatDisplay';

// Flight itinerary component
const FlightItinerary = React.memo<
  {
    flight: Flight;
    passenger: Passenger;
    findSeatOnFlight: (
      passengerId: string,
      flightId: string
    ) => SeatSelection | undefined;
  } & React.HTMLAttributes<HTMLDivElement>
>(({ flight, passenger, findSeatOnFlight, ...rest }) => {
  const seat = findSeatOnFlight(
    passenger.passenger_id ?? '',
    flight.segment_id
  );

  return (
    <div
      className="gr-flex gr-gap-4 gr-flex-col md:gr-flex-row md:gr-justify-between"
      key={flight.segment_id}
      {...rest}
    >
      <JourneyRoute
        fromCity={flight.departure_city_name}
        from={flight.departure_airport}
        toCity={flight.arrival_city_name}
        to={flight.arrival_airport}
      />
      <SeatDisplay seat={seat} />
    </div>
  );
});
FlightItinerary.displayName = 'FlightItinerary';

const BaggageDisplay = React.memo<{
  passengerBags: React.ReactNode;
}>(({ passengerBags }) => <>{passengerBags}</>);
BaggageDisplay.displayName = 'BaggageDisplay';

// #region Baggage journey component
const BaggageJourney = React.memo<
  {
    baggageJourney: Journey;
    passenger: Passenger;
    getPassengerBagsOnJourney: (
      passengerId: string,
      journeyIds: string[]
    ) => React.ReactNode;
  } & React.HTMLAttributes<HTMLDivElement>
>(({ baggageJourney, passenger, getPassengerBagsOnJourney, ...rest }) => (
  <div
    className="gr-flex gr-gap-4 gr-flex-col md:gr-flex-row md:gr-justify-between"
    key={baggageJourney.journey_id}
    {...rest}
  >
    <JourneyRoute
      from={baggageJourney.from}
      fromCity={baggageJourney.fromCity}
      to={baggageJourney.to}
      toCity={baggageJourney.toCity}
    />
    <BaggageDisplay
      passengerBags={getPassengerBagsOnJourney(
        passenger.passenger_id ?? '',
        baggageJourney.journey_ids ?? [baggageJourney.journey_id ?? '']
      )}
    />
  </div>
));
BaggageJourney.displayName = 'BaggageJourney';

// #region Passenger item component
const PassengerItem = React.memo<PassengerItemProps>(
  ({
    passenger,
    trigger,
    flights,
    baggageJourneys,
    findSeatOnFlight,
    getPassengerBagsOnJourney,
    ...rest
  }) => (
    <div
      key={passenger.passenger_id}
      className="gr-flex gr-flex-col gr-gap-4"
      {...rest}
    >
      <span
        id={`${rest.id}-passenger-name`}
        className="gr-font-medium gr-text-base"
      >
        {passenger.first_names} {passenger.surname}
      </span>

      {trigger === 'baggage' &&
        baggageJourneys?.map((baggageJourney, i) => (
          <BaggageJourney
            key={baggageJourney.journey_id}
            id={`${rest.id}-baggage-journey-${i}`}
            baggageJourney={baggageJourney}
            passenger={passenger}
            getPassengerBagsOnJourney={getPassengerBagsOnJourney}
          />
        ))}

      {trigger === 'seat' &&
        flights?.map((flight, i) => (
          <FlightItinerary
            key={flight.segment_id}
            id={`${rest.id}-flight-${i}`}
            flight={flight}
            passenger={passenger}
            findSeatOnFlight={findSeatOnFlight}
          />
        ))}
    </div>
  )
);
PassengerItem.displayName = 'PassengerItem';

// #region Helper function to format baggage display
const formatBaggageDisplay = (
  bagType: string,
  bagQuantity: number,
  weightUnit: string | undefined,
  displayName: string
) => {
  return bagType === 'weight_system'
    ? `1x ${bagQuantity}${weightUnit} ${displayName}`
    : `${bagQuantity}x ${displayName}`;
};

// #region Presentation component
const UpsellSummary: React.FC<UpsellSummaryProps> = (props) => {
  const {
    trigger = 'seat',
    passengers,
    flights,
    baggageJourneys,
    basket,
    baggageData,
    theme,
  } = props;

  // Memoized function to find a passenger's seat on a flight
  const findPassengerSeatOnFlight = useCallback(
    (passengerId: string, flightId: string) => {
      return basket?.seats.find(
        (seat) =>
          seat.passenger_id === passengerId && seat.segment_id === flightId
      );
    },
    [basket?.seats]
  );

  // Memoized function to get passenger bags on a journey
  const getPassengerBagsOnJourney = useCallback(
    (passengerId: string, journeyIds: string[]) => {
      const passengerBags = new Map(
        Object.entries(basket?.bags?.[passengerId] ?? {})
      );
      if (!passengerBags.size)
        return (
          <span className="gr-text-gray-550 gr-text-base">
            <FormattedMessage
              id="no_bags_selected"
              defaultMessage="No bags selected"
            />
          </span>
        );

      const bagsPerJourney = Object.values(
        baggageData.results?.products?.bag ?? {}
      ).filter((bag) =>
        journeyIds.some((journeyId) => {
          const bagJourneyIds = bag.product_details.journey_ids
            ? bag.product_details.journey_ids
            : [bag.product_details.journey_id];

          return bagJourneyIds.includes(journeyId);
        })
      );

      const passengerBagsOnJourney = bagsPerJourney.filter((bag) =>
        passengerBags.has(bag.product_id)
      );

      if (!passengerBagsOnJourney.length) {
        return (
          <span className="gr-text-gray-550 gr-text-base">
            No bags selected
          </span>
        );
      }

      return (
        <div className="gr-flex gr-flex-col gr-gap-4">
          {passengerBagsOnJourney.map((bag) => {
            const bagQuantity =
              typeof passengerBags.get(bag.product_id) === 'number'
                ? (passengerBags.get(bag.product_id) as number)
                : (passengerBags.get(bag.product_id) as { quantity: number })
                    .quantity;

            return (
              <span
                key={bag.product_id}
                className="gr-font-bold md:gr-text-right"
              >
                {formatBaggageDisplay(
                  bag.product_details.bag_type,
                  bagQuantity,
                  bag.product_details.weight_unit,
                  bag.product_details.display_name
                )}
              </span>
            );
          })}
        </div>
      );
    },
    [basket?.bags, baggageData.results?.products?.bag]
  );

  // Memoized function to determine if component should be shown
  const shouldShowComponent = useMemo(() => {
    if (!theme) return false;

    if (trigger === 'baggage') {
      return !!theme.baggageTrigger?.upsellSummary?.enabled;
    }

    if (trigger === 'seat') {
      return !!theme.seatMapTrigger?.upsellSummary?.enabled;
    }

    return false;
  }, [theme, trigger]);

  // Check if the component should be shown based on theme settings
  if (!trigger || !shouldShowComponent) {
    return null;
  }

  return (
    <div
      id="gordian-upsell-summary"
      className="gr-border-t-2 gr-border-0 gr-border-solid gr-border-gray-300 gr-p-6"
    >
      <div className="gr-flex gr-flex-col gr-gap-6 gr-px-2 gr-w-full">
        {passengers.listOfPassenger.map((passenger, i) => (
          <PassengerItem
            key={passenger.passenger_id}
            id={`summary-passenger-${i}`}
            passenger={passenger}
            trigger={trigger}
            flights={flights}
            baggageJourneys={baggageJourneys}
            findSeatOnFlight={findPassengerSeatOnFlight}
            getPassengerBagsOnJourney={getPassengerBagsOnJourney}
          />
        ))}
      </div>
    </div>
  );
};

const MemoizedUpsellSummary = React.memo(UpsellSummary);
MemoizedUpsellSummary.displayName = 'UpsellSummary';

const mapStateToProps = (state: {
  passengers: PassengerData;
  basket: Basket;
  itinerary: Itinerary;
  baggageJSON: { data: BaggageData };
  session: { theme: ThemeInterfaces.Theme };
}) => ({
  passengers: state.passengers,
  flights: state.itinerary.flights,
  baggageJourneys: state.itinerary.baggageJourneys,
  baggageData: state.baggageJSON.data,
  basket: state.basket,
  theme: state.session.theme,
});

const UpsellSummaryWrapper = connect(mapStateToProps)(MemoizedUpsellSummary);

export default UpsellSummaryWrapper;
